import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Ban, Paperclip, History, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Money, StatusBadge, Spinner } from "@/components/common";
import AttachmentPreview from "@/components/AttachmentPreview";
import AttachmentUploader from "@/components/AttachmentUploader";
import ReasonDialog from "@/components/ReasonDialog";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney, withinEditWindow } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Row = ({ label, children, mono }) => (
  <div className="py-2 border-b border-slate-100 grid grid-cols-[150px_1fr] gap-3 text-sm">
    <div className="text-slate-500 text-xs pt-0.5">{label}</div>
    <div className={`text-slate-800 break-words ${mono ? "num" : ""}`}>{children || "—"}</div>
  </div>
);

const fmtVal = (f, v) => (["before_vat", "vat_paid", "total"].includes(f) ? fmtMoney(v) : f === "invoice_date" ? fmtDate(v) : String(v ?? "—"));

const Revisions = ({ rows }) => (
  <ol className="relative border-l border-slate-200 ml-2 space-y-5" data-testid="bill-revisions">
    {rows.map((r) => (
      <li key={r.id} className="ml-5">
        <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-emerald-700 border-2 border-white" />
        <div className="text-xs text-slate-500 num">{fmtDateTime(r.changed_at)} · {r.changed_by_name} · IP {r.ip}</div>
        <div className="text-sm text-slate-700 mt-1"><span className="font-medium">Reason:</span> {r.reason}</div>
        <div className="mt-2 border border-slate-200 rounded overflow-hidden">
          {r.changes.map((c) => (
            <div key={c.field} className="grid grid-cols-3 text-xs border-b last:border-0 border-slate-100">
              <div className="px-2 py-1.5 bg-slate-50 text-slate-500">{c.field.replace(/_/g, " ")}</div>
              <div className="px-2 py-1.5 text-red-700 line-through num truncate">{fmtVal(c.field, c.old)}</div>
              <div className="px-2 py-1.5 text-emerald-700 num truncate">{fmtVal(c.field, c.new)}</div>
            </div>
          ))}
        </div>
      </li>
    ))}
    {!rows.length && <li className="ml-5 text-sm text-slate-400">No corrections recorded. This record is in its original state.</li>}
  </ol>
);

export default function BillDrawer({ billId, open, onOpenChange, onChanged }) {
  const { user, settings } = useAuth();
  const nav = useNavigate();
  const [bill, setBill] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [adding, setAdding] = useState([]);

  const load = useCallback(async () => {
    if (!billId) return;
    setBill(null);
    try { const { data } = await api.get(`/bills/${billId}`); setBill(data); } catch (e) { toast.error(formatErr(e)); }
  }, [billId]);

  useEffect(() => { if (open) { load(); setAdding([]); } }, [open, load]);

  const update = (data) => { setBill(data); onChanged?.(); };
  const canEdit = bill && bill.record_status === "active" && (user.role === "super_admin" || (user.role === "staff" && settings?.staff_can_edit_own && bill.created_by === user.id && withinEditWindow(bill.created_at)));
  const active = bill?.attachments?.filter((a) => a.status === "active").length || 0;

  const attach = async () => {
    try {
      const { data } = await api.post(`/bills/${bill.id}/attachments`, { attachment_ids: adding.map((a) => a.id) });
      setAdding([]); update(data); toast.success("Attachments linked");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[720px] p-0 bg-white overflow-y-auto" data-testid="bill-detail-drawer">
        {!bill ? <><SheetTitle className="sr-only">Loading bill</SheetTitle><SheetDescription className="sr-only">Bill details</SheetDescription><Spinner /></> : (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="num text-xs text-slate-500" data-testid="drawer-record-no">{bill.record_no}</span>
                <StatusBadge status={bill.record_status} testid="drawer-record-status" />
                {active === 0 && <StatusBadge status="missing" />}
              </div>
              <SheetTitle className="text-xl font-display">INV {bill.invoice_number} · {bill.vendor_name}</SheetTitle>
              <div className="text-sm text-slate-500">{bill.client_name}</div>
              <SheetDescription className="sr-only">Bill record details</SheetDescription>
              <div className="flex gap-2 pt-2">
                {canEdit && <Button size="sm" variant="outline" onClick={() => nav(`/bills/${bill.id}/edit`)} data-testid="drawer-edit-button"><Pencil className="w-3.5 h-3.5 mr-1.5" />Correct entry</Button>}
                {user.role === "super_admin" && bill.record_status === "active" && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setVoiding(true)} data-testid="drawer-void-button"><Ban className="w-3.5 h-3.5 mr-1.5" />Void record</Button>}
              </div>
            </SheetHeader>
            <div className="grid grid-cols-3 border-b border-slate-200">
              {[["Before VAT", bill.before_vat], ["VAT Amount", bill.vat_paid], ["Total", bill.total]].map(([l, v], i) => (
                <div key={l} className={`px-6 py-4 ${i < 2 ? "border-r border-slate-200" : "bg-emerald-50/60"}`}>
                  <div className="eyebrow">{l}</div>
                  <div className="mt-1 text-lg" data-testid={`drawer-amount-${i}`}><Money value={v} strong={i === 2} /></div>
                </div>
              ))}
            </div>
            <div className="px-6 py-5">
              {bill.record_status === "voided" && <div className="mb-4 text-sm border border-red-200 bg-red-50 text-red-800 rounded px-3 py-2">Voided by {bill.voided_by_name} on {fmtDateTime(bill.voided_at)} — {bill.void_reason}</div>}
              <Tabs defaultValue="details">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="details" data-testid="drawer-tab-details"><FileText className="w-3.5 h-3.5 mr-1.5" />Details</TabsTrigger>
                  <TabsTrigger value="attachments" data-testid="drawer-tab-attachments"><Paperclip className="w-3.5 h-3.5 mr-1.5" />Attachments ({active})</TabsTrigger>
                  <TabsTrigger value="history" data-testid="drawer-tab-history"><History className="w-3.5 h-3.5 mr-1.5" />History ({bill.revisions.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="details" className="mt-4">
                  <Row label="Client">{bill.client_name} <span className="num text-xs text-slate-400">{bill.client_code}</span></Row>
                  <Row label="Invoice date" mono>{fmtDate(bill.invoice_date)}</Row>
                  <Row label="Invoice number" mono>{bill.invoice_number}</Row>
                  <Row label="Vendor">{bill.vendor_name}</Row>
                  <Row label="Vendor VAT no." mono>{bill.vendor_vat_number}</Row>
                  <Row label="Item">{bill.item_name}</Row>
                  <Row label="Sponsor / Customer">{bill.sponsor_name}</Row>
                  <Row label="Currency">{bill.currency}</Row>
                  <Row label="Entered by">{bill.created_by_name}</Row>
                  <Row label="Entered at (AST)" mono>{fmtDateTime(bill.created_at)} · IP {bill.created_ip}</Row>
                  {bill.updated_at && <Row label="Last corrected" mono>{fmtDateTime(bill.updated_at)} · {bill.updated_by_name} · IP {bill.updated_ip}</Row>}
                </TabsContent>
                <TabsContent value="attachments" className="mt-4 space-y-3">
                  {canEdit && (
                    <div className="panel p-3">
                      <AttachmentUploader files={adding} onChange={setAdding} testid="drawer-attachment-upload" />
                      {adding.length > 0 && <Button size="sm" className="mt-3 bg-emerald-800 hover:bg-emerald-900" onClick={attach} data-testid="drawer-link-attachments"><Plus className="w-3.5 h-3.5 mr-1" />Link {adding.length} file(s) to record</Button>}
                    </div>
                  )}
                  {bill.attachments.map((a, i) => <AttachmentPreview key={a.id} att={a} index={i} onRemove={canEdit ? setRemoving : null} />)}
                  {!bill.attachments.length && <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded px-3 py-3">No source document is linked to this record.</div>}
                </TabsContent>
                <TabsContent value="history" className="mt-4"><Revisions rows={bill.revisions} /></TabsContent>
              </Tabs>
            </div>
          </>
        )}
        <ReasonDialog open={voiding} onOpenChange={setVoiding} title="Void this record?" destructive confirmLabel="Void record" testid="void-dialog"
          description="Financial records are never deleted. The record stays in history marked as voided."
          onConfirm={async (reason) => {
            try { await api.post(`/bills/${bill.id}/void`, { reason }); toast.success("Record voided"); setVoiding(false); load(); onChanged?.(); } catch (e) { toast.error(formatErr(e)); }
          }} />
        <ReasonDialog open={!!removing} onOpenChange={() => setRemoving(null)} title="Remove attachment?" destructive confirmLabel="Remove" testid="remove-attachment-dialog"
          description="The file is kept in storage and history; it is unlinked from the active record."
          onConfirm={async (reason) => {
            try { const { data } = await api.post(`/bills/${bill.id}/attachments/${removing.id}/remove`, { reason }); setRemoving(null); update(data); } catch (e) { toast.error(formatErr(e)); }
          }} />
      </SheetContent>
    </Sheet>
  );
}
